import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

interface ScrollablePaginationProps {
  children: React.ReactNode;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  className?: string;
  threshold?: number;
  rootMargin?: string;
  initialLoadComplete?: boolean;
}

export default function ScrollablePagination({ children, onLoadMore, hasMore, isLoading, className = "", threshold = 1, rootMargin = "50px", initialLoadComplete = true }: ScrollablePaginationProps) {
  const fetchBoundaryRef = useRef<HTMLDivElement>(null);
  const [isTriggered, setIsTriggered] = useState(false);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const trigger = entries[0].isIntersecting;
      const shouldLoadMore = trigger && !isLoading && hasMore && !isTriggered && initialLoadComplete;

      if (shouldLoadMore) {
        setIsTriggered(true);
        onLoadMore();
      }
    },
    [isLoading, hasMore, onLoadMore, isTriggered, initialLoadComplete]
  );

  // Reset triggered state when loading completes
  useEffect(() => {
    if (!isLoading) {
      setIsTriggered(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!fetchBoundaryRef.current) {
      return;
    }

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin,
      threshold,
    });

    observer.observe(fetchBoundaryRef.current);

    return () => {
      observer.disconnect();
    };
  }, [observerCallback, rootMargin, threshold]);

  // Layout effect to trigger load if sentinel is visible on first render
  useLayoutEffect(() => {
    if (fetchBoundaryRef.current) {
      const rect = fetchBoundaryRef.current.getBoundingClientRect();
      if (rect.top < window.innerHeight && hasMore && !isLoading && !isTriggered && initialLoadComplete) {
        setIsTriggered(true);
        onLoadMore();
      }
    }
  }, [children, hasMore, isLoading, isTriggered, onLoadMore, initialLoadComplete]);

  const getMessage = () => {
    if (!hasMore) {
      return "";
    }
    if (isLoading) {
      return "Loading more...";
    }
    return "Scroll for more...";
  };

  return (
    <div className={className}>
      {children}
      <div
        ref={fetchBoundaryRef}
        className="flex justify-center py-4 text-gray-500 text-sm"
      >
        {getMessage()}
      </div>
    </div>
  );
}
